// Future rigs and skins can be added to the TUTOR_OPTIONS array for previewing and demos
// if not implemented, set prohibited to true on push as a feature flag until fully implemented

import { GetBrandedDefaultTutorRig, GetBrandedDefaultTutorSkin, GetBrandKey } from './BrandingService';

// Tutor Previews
import AmiraTutorPreview from '../images/tutors/AmiraTutorPreview.png';
import Amira2TutorPreview from '../images/tutors/Amira2TutorPreview.png';
import MaleTutorPreview from '../images/tutors/MaleTutorPreview.png';
import BunnyTutorPreview from '../images/tutors/BunnyTutorPreview.png';
import PuppyTutorPreview from '../images/tutors/PuppyTutorPreview.png';

const TUTOR_OPTIONS = [
  {
    image: AmiraTutorPreview,
    rig: "Amira",
    skin: "Amira"
  },
  // Maya will never be in the tutor picker, and therefore does not have a preview image
  {
    image: null,
    rig: "Amira",
    skin: "Maya",
    // This means that the tutor will only be available for the NWEA brand
    limitedBrands: ["nwea-map-tutor"]
  },
  {
    image: Amira2TutorPreview,
    rig: "Amira2",
    skin: "Amira",
    // prohibited: true,
  },
  {
    image: MaleTutorPreview,
    rig: "Amira2",
    skin: "Guy",
		// TODO: Remove this once we have a male tutor
    prohibited: true,
  },
  {
    image: BunnyTutorPreview,
    rig: "Amira2",
    skin: "Rabbit",
    prohibited: true,
  },
  {
    image: PuppyTutorPreview,
    rig: "Amira2",
    skin: "Dawg",
    prohibited: true,
  }
];

// Helper function to generate CDN asset paths
export function getAssetURL(rig, fileName) {
  return `https://${process.env.ASSET_ROOT}/amira-rigs/${rig}/${fileName}`;
}

export function getRigSkeletonName(rig) {
  return {
    Amira: 'Amira',
    Amira2: 'Amira',
  }[rig];
}

export function getRigAtlasName(rig) {
  return {
    Amira: 'Amira',
    Amira2: 'Amira',
  }[rig];
}

export function getRigTexturesArray(rig) {
  return {
    Amira: [ 'Amira.png', 'Amira2.png', ],
    Amira2: [ 
			'Amira.png', 'Amira2.png', 'Amira3.png', 'Amira4.png', 'Amira5.png', 
			'Amira6.png', 'Amira7.png', 'Amira8.png', 'Amira9.png', 'Amira10.png',
			'Amira11.png', 'Amira12.png', 'Amira13.png', 'Amira14.png', 'Amira15.png',
			'Amira16.png', 'Amira17.png', 'Amira18.png', 'Amira19.png', 'Amira20.png',
		],
  }[rig];
}

// Get prohibited rigs for the current user
export function getDistrictProhibitedRigs(userData) {
  if (!userData) return [];
  return userData.prohibitedRigs || [];
}

// Get prohibited skins for the current user
export function getDistrictProhibitedSkins(userData) {
  if (!userData) return {};
  return userData.prohibitedSkins || {};
}

// Get all available tutor options
export function getSelectableTutors(userData) {
  const prohibitedRigs = getDistrictProhibitedRigs(userData);
  const prohibitedSkins = getDistrictProhibitedSkins(userData);

  return TUTOR_OPTIONS.filter(tutor => {
    if (tutor.limitedBrands && !tutor.limitedBrands.includes(GetBrandKey())) {
      return false;
    } else if (tutor.prohibited) {
      return false;
    } else if (prohibitedRigs.includes(tutor.rig)) {
      return false;
    } else if (prohibitedSkins[tutor.rig]?.includes(tutor.skin)) {
      return false;
    }
    return true;
  });
}

// Get the character configuration based on branding and user preferences
export function GetCharacterConfiguration(userData) {
  // First, we grab the user's selected tutor rig and skin from the userData
  const userTutorRig = userData ? userData.selectedTutorRig : null;
  const userTutorSkin = userData ? userData.selectedTutorSkin : null;

  // We check if the user's selected tutor rig and skin exist in the TUTOR_OPTIONS array
  const userSelectedTutorOption = TUTOR_OPTIONS.find(
    tutor => tutor.rig === userTutorRig && tutor.skin === userTutorSkin
  );
  const userSelectedTutorOptionNotForCurrentBrand = userSelectedTutorOption && userSelectedTutorOption.limitedBrands && !userSelectedTutorOption.limitedBrands.includes(GetBrandKey());

  // Then, we mark the rig and or skin as prohibited if
  // 1. The tutor option does not exist
  // 2. The tutor is not available in app yet (feature flag)
  // 3. The selected tutor is not available for the current brand (shouldn't happen, but just in case)
  // 4. The rig is prohibited by the district
  // 5. The skin is prohibited by the district
  const userRigIsProhibited = !userSelectedTutorOption || userSelectedTutorOption.prohibited || userSelectedTutorOptionNotForCurrentBrand || getDistrictProhibitedRigs(userData).includes(userTutorRig);
  const userSkinIsProhibited = !userSelectedTutorOption || userSelectedTutorOption.prohibited || userSelectedTutorOptionNotForCurrentBrand || getDistrictProhibitedSkins(userData)[userTutorRig]?.includes(userTutorSkin);
  
  // Then, we grab the default tutor rig and skin from the branding service as our fallback
  const brandedTutorRig = GetBrandedDefaultTutorRig();
  const brandedTutorSkin = GetBrandedDefaultTutorSkin();
  let tutorRig = brandedTutorRig;
  let tutorSkin = brandedTutorSkin;

  // If the users selections exist and are not prohibited, we use the users selections
  // Otherwise, we maintain the branded default tutor rig and skin
  if (userTutorRig && !userRigIsProhibited && userTutorSkin && !userSkinIsProhibited) {
    tutorRig = userTutorRig;
    tutorSkin = userTutorSkin;
  }
    
  // Extract folder name from assetPath for CDN path construction
  const rigFolder = tutorRig;
  
  // Get the texture files for this rig
  const textureFiles = getRigTexturesArray(tutorRig);
  const cdnTextureURLs = textureFiles.map(file => getAssetURL(rigFolder, file));
  
  return {
    rig: tutorRig,
    skin: tutorSkin,
    // Add CDN paths for skeleton, atlas, and texture files
    cdnSkeletonURL: getAssetURL(rigFolder, `${getRigSkeletonName(tutorRig)}.skel`),
    cdnAtlasURL: getAssetURL(rigFolder, `${getRigAtlasName(tutorRig)}.atlas`),
    cdnTextureURLs: cdnTextureURLs,
    scale: 0.70,
    positions: {
      default: { x: 1650, y: 340 },
      onScreenKeyboard: { x: 1650, y: 0 }
    }
  };
}